import { ReactElement, useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import FormInput from '../../components/FormInput';
import FormEntry from '../../components/FormEntry';
import Button from '../../components/Button';
import SectionHeader from '../../components/SectionHeader';
import { HiOutlineCheckCircle, HiOutlinePencil, HiOutlineInformationCircle } from 'react-icons/hi2';
import toast from 'react-hot-toast';
import Spinner from '../../components/Spinner';
import { useUnsavedChangesWarning } from '../../hooks/useUnsavedChangesWarning';
import { CalibrationParameters } from '../../types/CalibrationParameters';
import { Config } from '../../types/Config';

// Extended config type that includes all calibration fields from session
type SessionConfig = Config & CalibrationParameters;

// Extended type for the form that includes copy-related fields
type CalibrationFormData = CalibrationParameters & {
  source_session_id?: string;
  copy_calibration_id?: string;
};
import { useConfigWithGates, useCreateConfig, useSetConfig, useUpdateConfigId } from '../../hooks/useConfigs';
import { useSessions, useSessionCalcData } from '../../hooks/useSessions';
import { SessionAnalysisTypeUtils } from '../../types/SessionAnalysisType';
import { useChainModels } from '../../hooks/useChainModels';

export default function CalibrationView(): ReactElement {
  const { calibrationId, sessionId } = useParams<{ calibrationId: string; sessionId?: string }>();
  if (!calibrationId) {
    throw new Error('Missing route parameter: calibrationId');
  }

  const navigate = useNavigate();
  const location = useLocation();
  const isNew = calibrationId === 'new';

  // Check if we're in a session context
  const isInSessionContext =
    !!sessionId || (location.pathname.includes('/sessions/') && location.pathname.includes('/calibrations/'));

  // Extract sessionId from URL if not in params
  const extractedSessionId = useMemo(() => {
    if (sessionId) return sessionId;
    if (isInSessionContext) {
      const pathParts = location.pathname.split('/');
      const sessionIndex = pathParts.findIndex((part) => part === 'sessions');
      if (sessionIndex !== -1 && pathParts[sessionIndex + 1]) {
        return pathParts[sessionIndex + 1];
      }
    }
    return null;
  }, [sessionId, isInSessionContext, location.pathname]);

  // Fetch session data when in session context
  const { data: sessionData } = useSessionCalcData(extractedSessionId || 'no-session-selected');
  const { data: sessions } = useSessions();

  // Prioritize calibration data from session when available, fallback to direct API
  const calibrationFromSession = useMemo((): SessionConfig | null => {
    if (!sessionData?.configs || !calibrationId || isNew) return null;
    return (sessionData.configs.find((config) => config.id === calibrationId) as SessionConfig) || null;
  }, [sessionData, calibrationId, isNew]);

  // Only fetch from API if not available from session and not new
  const shouldFetchFromApi = !calibrationFromSession && !isNew;
  const { data: calibrationFromApi } = useConfigWithGates(calibrationId, { enabled: shouldFetchFromApi });

  const calibration: CalibrationParameters | SessionConfig | null =
    calibrationFromSession || calibrationFromApi || null;
  const [speedInputType, setSpeedInputType] = useState('1');
  const [powerInputType, setPowerInputType] = useState('1');
  const formMethods = useForm<CalibrationFormData>({ defaultValues: calibration || undefined });
  const { setValue, getValues, reset, watch } = formMethods;

  // Create parentCalibrations lookup from baseline chain
  const parentCalibrations: { [key: string]: any } = {};

  if (calibration?.baseline_obj) {
    let currentBaseline = calibration.baseline_obj;

    while (currentBaseline) {
      parentCalibrations[currentBaseline.id] = currentBaseline;
      currentBaseline = currentBaseline.baseline_obj;
    }
  }

  // State for tracking parameter modes
  const [parameterModes, setParameterModes] = useState<{ [key: string]: 'manual' | 'inherit' | 'automatic' }>({});
  // State for tracking which fields are in edit mode
  const [editingFields, setEditingFields] = useState<Set<string>>(new Set());
  // State for tracking tooltip visibility
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
  // Chain model data
  const { data: chainModels } = useChainModels();
  // State for copy source selection
  const [copySource, setCopySource] = useState<'default' | 'session' | null>(null);
  const [selectedSessionId, setSelectedSessionId] = useState<string>('');
  const [sessionConfigs, setSessionConfigs] = useState<any[]>([]);

  // Fetch session calc data when a session is selected
  const { data: selectedSessionCalcData } = useSessionCalcData(selectedSessionId || 'no-session-selected');

  const activeParameters = [
    'power_meter_slope',
    'cp_corr',
    'yaw_offset',
    'wheel_circumference',
    'cda_ref',
    'chest_ref',
    'head_ref',
    'chest_slope',
    'head_slope',
    'airspeed_ref',
    'airspeed_slope',
    'xw_wheel_compensation',
    'xw_para_slope',
    'xw_stall_slope',
    'xw_stall_angle',
    'crr',
    'combined_mass',
  ];

  // Field configuration for decimal places, units, and info
  const fieldConfig: Record<string, { decimals: number; units: string; info?: string }> = {
    cp_corr: {
      decimals: 3,
      units: '',
      info:
        'Cp Correction, compensation for air stagnation ahead of probe.' +
        ' Typical values should be between 1.05 - 1.50' +
        ' Automatic is preferred initial setting.',
    },
    yaw_offset: {
      decimals: 1,
      units: 'deg.',
      info:
        'Angle correction for probe misalignment.' +
        ' Typical values should be +/- 5°' +
        ' Automatic is preferred initial setting.',
    },
    wheel_circumference: {
      decimals: 3,
      units: 'm',
      info:
        'Wheel Circumference in meters.' +
        ' Typical values should be 2.050 to 2.280m' +
        ' Automatic is preferred initial setting.',
    },
    cda_ref: {
      decimals: 3,
      units: '1/m²',
      info: 'Reference CdA value used for Crr measurment' + ' Automatic is preferred initial setting.',
    },
    chest_ref: { decimals: 1, units: 'mm' },
    head_ref: { decimals: 1, units: 'mm' },
    chest_slope: { decimals: 5, units: 'CdA / mm' },
    head_slope: { decimals: 5, units: 'CdA / mm' },
    airspeed_ref: { decimals: 1, units: 'm/s' },
    airspeed_slope: { decimals: 5, units: 'CdA / m/s' },
    xw_wheel_compensation: { decimals: 0, units: 'mm' },
    xw_para_slope: { decimals: 5, units: '' },
    power_meter_slope: { decimals: 3, units: '' },
    crr: { decimals: 5, units: 's/m' },
    combined_mass: { decimals: 1, units: 'kg' },
  };

  // Reset form when calibration changes
  useEffect(() => {
    if (calibration) {
      reset(calibration);
      // Update speed input type based on calibration data
      if (calibration.gps_speed) {
        setSpeedInputType('GPS');
      } else if (calibration.alt_spd === 0) {
        setSpeedInputType('1');
      } else if (calibration.alt_spd === 1) {
        setSpeedInputType('2');
      } else {
        setSpeedInputType('1');
      }

      // Update power input type based on calibration data
      if (calibration.combine_power) {
        setPowerInputType('Combine');
      } else if (calibration.alt_power === 0) {
        setPowerInputType('1');
      } else if (calibration.alt_power === 1) {
        setPowerInputType('2');
      } else {
        setPowerInputType('1');
      }

      const newParameterModes: { [key: string]: 'manual' | 'inherit' | 'automatic' } = {};

      activeParameters.forEach((field) => {
        let mode: 'manual' | 'inherit' | 'automatic' = 'manual'; // Default to manual instead of inherit

        if (calibration.parameters_manual?.includes(field)) {
          mode = 'manual';
        } else if (calibration.parameters_inherited?.[field]) {
          mode = 'inherit';
        } else if (calibration.autocal_list?.includes(field)) {
          mode = 'automatic';
        }
        // If none of the above conditions match, it stays 'manual' (the default)

        newParameterModes[field] = mode;
      });

      setParameterModes(newParameterModes);

      // Load chain model state from drivetrain
      const ftc = calibration?.drivetrain?.front_tooth_count;
      if (ftc) {
        setValue('front_tooth_count' as any, ftc);
      }

      // Load chain model selection
      if (calibration?.model_chain_id) {
        setValue('model_chain_id', calibration.model_chain_id);
      }
    }
  }, [calibration]);

  const setCalibration = useSetConfig();
  const updateCalibration = isNew ? useCreateConfig() : useUpdateConfigId(calibrationId || '');

  // Use unsaved changes warning hook
  const { clearUnsavedChanges } = useUnsavedChangesWarning({
    enabled: true,
    message: 'You have unsaved changes to this calibration. Are you sure you want to leave?',
  });

  const setSpeedInputTypeWrapper = (value: string) => {
    if (value === 'GPS') {
      setValue('gps_speed', true, { shouldDirty: true });
    } else {
      setValue('gps_speed', false, { shouldDirty: true });
      if (value === '1' || value === '2') {
        setValue('alt_spd', value === '1' ? 0 : 1);
      }
    }
    setSpeedInputType(value);
  };

  const setPowerInputTypeWrapper = (value: string) => {
    if (value === 'Combine') {
      setValue('combine_power', true, { shouldDirty: true });
    } else {
      setValue('combine_power', false, { shouldDirty: true });
      if (value === '1' || value === '2') {
        setValue('alt_power', value === '1' ? 0 : 1);
      }
    }
    setPowerInputType(value);
  };

  const handleParameterModeChange = (
    fieldName: keyof CalibrationParameters,
    mode: 'manual' | 'inherit' | 'automatic',
  ) => {
    setParameterModes((prev) => ({
      ...prev,
      [fieldName]: mode,
    }));

    // Update form data based on mode selection
    // This would typically update parameters_manual array and autocal_branch
    // For now, just mark the field as dirty
    setValue(fieldName, getValues(fieldName), { shouldDirty: true });
  };

  const toggleEditMode = (fieldName: string) => {
    setEditingFields((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(fieldName)) {
        newSet.delete(fieldName);
      } else {
        newSet.add(fieldName);
      }
      return newSet;
    });
  };

  const lockCalibration = () => {
    const newParameterModes: { [key: string]: 'manual' | 'inherit' | 'automatic' } = {};

    setValue('autocal_lock', true, { shouldDirty: true });
    activeParameters.forEach((field) => {
      newParameterModes[field] = 'manual';

      // Get the current value (either inherited or current form value)
      const currentMode = parameterModes[field];
      if (currentMode === 'inherit') {
        const inheritedData = calibration?.parameters_inherited?.[field];
        const inheritedValue = inheritedData?.[0];
        if (inheritedValue !== null && inheritedValue !== undefined) {
          setValue(field as keyof CalibrationParameters, inheritedValue, { shouldDirty: true });
        }
      } else if (currentMode === 'automatic') {
        const inheritedData = calibration?.parameters_inherited?.[field];
        const inheritedValue = inheritedData?.[0];
        if (inheritedValue !== null && inheritedValue !== undefined) {
          setValue(field as keyof CalibrationParameters, inheritedValue, { shouldDirty: true });
        }
      }
    });

    setParameterModes(newParameterModes);
    toast.success('All parameters locked to manual mode');
  };

  const clearCalibration = () => {
    if (!calibration?.baseline_id) {
      toast.error('Cannot clear calibration: no baseline calibration available');
      return;
    }

    setValue('autocal_lock', false, { shouldDirty: true });

    // Use functional state update to ensure we get the latest state
    setParameterModes((prevModes) => {
      const newParameterModes: { [key: string]: 'manual' | 'inherit' | 'automatic' } = { ...prevModes };

      activeParameters.forEach((field) => {
        newParameterModes[field] = 'inherit';
        // Clear the form value to null so it inherits
        setValue(field as keyof CalibrationParameters, null, { shouldDirty: true });
      });

      return newParameterModes;
    });

    // Force a re-render by updating a different state
    setEditingFields(new Set());

    toast.success('All parameters set to inherit mode');
  };

  // Watch for session selection changes in copy mode
  useEffect(() => {
    if (copySource !== 'session') return;

    const subscription = formMethods.watch((value, { name }) => {
      if (name === 'source_session_id' && value.source_session_id) {
        setSelectedSessionId(value.source_session_id);
      }
    });

    return () => subscription.unsubscribe();
  }, [formMethods.watch, copySource]);

  // Update available configs when session is selected
  useEffect(() => {
    if (copySource !== 'session') return;

    if (selectedSessionId && selectedSessionId !== 'no-session-selected') {
      if (selectedSessionCalcData && selectedSessionCalcData.configs) {
        setSessionConfigs(selectedSessionCalcData.configs);
      } else {
        setSessionConfigs([]);
      }
    }

    // Clear calibration selection when session changes
    if (selectedSessionId) {
      setValue('copy_calibration_id', '', { shouldDirty: true });
    }
  }, [selectedSessionCalcData, selectedSessionId, setValue, copySource]);

  const applyCopiedCalibration = () => {
    const copyCalibrationId = getValues('copy_calibration_id');
    if (!copyCalibrationId) {
      toast.error('No calibration selected to copy from');
      return;
    }

    // Find the selected configuration from sessionConfigs
    const selectedConfig = sessionConfigs.find((config) => config.id === copyCalibrationId);
    if (!selectedConfig) {
      toast.error('Selected calibration not found');
      return;
    }

    const newParameterModes: { [key: string]: 'manual' | 'inherit' | 'automatic' } = {};

    // Copy all active parameters from the selected configuration and set to manual
    activeParameters.forEach((field) => {
      newParameterModes[field] = 'manual';

      // Get the value from the selected configuration
      const configValue = selectedConfig[field];
      if (configValue !== null && configValue !== undefined) {
        setValue(field as keyof CalibrationParameters, configValue, { shouldDirty: true });
      }
    });

    // Handle special fields that might not be in activeParameters
    if (selectedConfig.combined_mass !== null && selectedConfig.combined_mass !== undefined) {
      setValue('combined_mass', selectedConfig.combined_mass, { shouldDirty: true });
    }

    // Copy drivetrain / chain model state
    const ftc = selectedConfig.drivetrain?.front_tooth_count;
    if (ftc) {
      setValue('front_tooth_count' as any, ftc, {
        shouldDirty: true,
      });
    }

    // Copy chain model selection
    setValue('model_chain_id',
      selectedConfig.model_chain_id || null,
      { shouldDirty: true });

    setParameterModes(newParameterModes);
    toast.success(
      'Applied calibration: '
      + `${selectedConfig.description || selectedConfig.id}`,
    );
  };

  const getParameterModeOptions = (fieldName: string) => {
    const options = [
      { value: 'manual', label: 'Manual' },
      { value: 'inherit', label: 'Inherit' },
    ];

    // Only add automatic option for specific fields
    if (['cp_corr', 'wheel_circumference', 'yaw_offset'].includes(fieldName)) {
      options.push({ value: 'automatic', label: 'Automatic' });
    }

    return options;
  };

  const renderFieldWithMode = (fieldName: keyof CalibrationParameters, label: string, inputProps: any = {}) => {
    const currentMode = parameterModes[fieldName] || 'manual';

    const isInherit = currentMode === 'inherit';
    const isEditing = editingFields.has(fieldName);

    // Get inherited value and parent calibration info
    const inheritedData = calibration?.parameters_inherited?.[fieldName];
    const inheritedValue = inheritedData?.[0];
    const parentCalibrationId = inheritedData?.[1];

    const parentCalibration = parentCalibrationId && parentCalibrations[parentCalibrationId];
    const parentDescription = parentCalibration?.description;

    // Get the current value to display
    // For inherit mode, always show inherited value if available, otherwise show form value
    const currentValue =
      isInherit && inheritedValue !== undefined && inheritedValue !== null ? inheritedValue : getValues(fieldName);

    // Get field configuration for decimals, units, and info
    const config = fieldConfig[fieldName] || inputProps;
    const decimals = inputProps.decimals ?? config.decimals ?? 3;
    const units = inputProps.units ?? config.units ?? '';
    const info = inputProps.info ?? config.info;

    // Format the value for display
    const formatValue = (value: any) => {
      if (value === null || value === undefined || value === '') {
        return '-';
      }
      if (typeof value === 'number' && !isEditing) {
        return value.toFixed(decimals);
      }
      return value;
    };

    // Format the mode display text
    const getModeDisplay = () => {
      if (currentMode === 'inherit' && parentDescription) {
        return `Inherit (from: ${parentDescription})`;
      }
      if (currentMode === 'inherit') {
        // Show inherit even without parent description
        return 'Inherit';
      }
      if (currentMode === 'automatic') {
        return 'Automatic';
      }
      return 'Manual';
    };

    // Get background color class for mode
    const getModeBackgroundClass = () => {
      if (currentMode === 'manual') {
        return 'cr-bg-green-100';
      }
      if (currentMode === 'inherit') {
        return 'cr-bg-gray-100';
      }
      if (currentMode === 'automatic') {
        return 'cr-bg-blue-100';
      }
      return '';
    };

    return (
      <FormEntry label={label}>
        <div className="cr-flex cr-items-center cr-gap-2">
          {/* Edit button */}
          <button
            type="button"
            onClick={() => toggleEditMode(fieldName)}
            className="cr-rounded cr-p-1 hover:cr-bg-gray-100"
            title="Edit"
          >
            <HiOutlinePencil className="cr-h-5 cr-w-5 cr-text-gray-600" />
          </button>

          {isEditing ? (
            // Edit mode: show dropdown and input field
            <>
              <select
                value={currentMode}
                onChange={(e) =>
                  handleParameterModeChange(fieldName, e.target.value as 'manual' | 'inherit' | 'automatic')
                }
                disabled={fieldName === 'elevation_difference' && currentMode === 'inherit'}
                className="cr-min-w-[120px] cr-rounded cr-border cr-border-gray-300 cr-px-2 cr-py-1 cr-text-sm"
              >
                {getParameterModeOptions(fieldName).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              {currentMode === 'manual' ? (
                <FormInput fieldName={fieldName} customClass="cr-w-[60%]" {...inputProps} />
              ) : (
                <input
                  type={inputProps.inputType || 'text'}
                  value={formatValue(currentValue)}
                  disabled={true}
                  className="cr-w-[25%] cr-rounded cr-border cr-border-gray-300 cr-bg-gray-100 cr-px-3 cr-py-2 cr-text-sm cr-text-gray-600"
                />
              )}

              {/* Units in edit mode */}
              {units && <span className="cr-text-sm cr-text-gray-600">{units}</span>}
            </>
          ) : (
            // Display mode: show mode and value as text
            <>
              <span
                className={`cr-min-w-[120px] cr-rounded cr-px-2 cr-py-1 cr-text-sm cr-font-medium cr-text-gray-700
               ${getModeBackgroundClass()}`}
              >
                {getModeDisplay()}
              </span>
              <span className="cr-text-sm cr-text-gray-900">{formatValue(currentValue)}</span>

              {/* Units in display mode */}
              {units && <span className="cr-text-sm cr-text-gray-600">{units}</span>}
            </>
          )}

          {/* Info button */}
          {info && (
            <div className="cr-relative">
              <button
                type="button"
                onMouseEnter={() => setActiveTooltip(fieldName)}
                onMouseLeave={() => setActiveTooltip(null)}
                className="cr-rounded cr-p-1 hover:cr-bg-gray-100"
                title={info}
              >
                <HiOutlineInformationCircle className="cr-h-4 cr-w-4 cr-text-blue-500" />
              </button>

              {/* Tooltip */}
              {activeTooltip === fieldName && (
                <div className="cr-absolute cr-bottom-full cr-right-0 cr-z-10 cr-mb-2 cr-w-64 cr-rounded cr-bg-gray-800 cr-px-3 cr-py-2 cr-text-sm cr-text-white cr-shadow-lg">
                  {info}
                  <div className="cr-absolute cr-right-4 cr-top-full cr-h-0 cr-w-0 cr-border-l-4 cr-border-r-4 cr-border-t-4 cr-border-l-transparent cr-border-r-transparent cr-border-t-gray-800"></div>
                </div>
              )}
            </div>
          )}
        </div>
      </FormEntry>
    );
  };

  const getBackLink = () => {
    if (isInSessionContext) {
      if (sessionId) {
        return { to: `/sessions/${sessionId}`, text: 'Back to Session' };
      } else {
        // Extract sessionId from URL if not in params
        const pathParts = location.pathname.split('/');
        const sessionIndex = pathParts.findIndex((part) => part === 'sessions');
        if (sessionIndex !== -1 && pathParts[sessionIndex + 1]) {
          return { to: `/sessions/${pathParts[sessionIndex + 1]}`, text: 'Back to Session' };
        }
      }
    }
    return { to: '/calibrations', text: 'Back to Calibrations' };
  };

  const redirectAfterSave = () => {
    if (isInSessionContext) {
      if (sessionId) {
        navigate(`/sessions/${sessionId}`);
      } else {
        // Extract sessionId from URL if not in params
        const pathParts = location.pathname.split('/');
        const sessionIndex = pathParts.findIndex((part) => part === 'sessions');
        if (sessionIndex !== -1 && pathParts[sessionIndex + 1]) {
          navigate(`/sessions/${pathParts[sessionIndex + 1]}`);
        } else {
          navigate('/calibrations');
        }
      }
    } else {
      if (isNew) {
        // For new calibrations, redirect to the created calibration
        return; // This will be handled in the save function
      } else {
        // For existing calibrations, stay on the same page
        return;
      }
    }
  };

  const save = async () => {
    try {
      const formValues = getValues();
      const cleanedValues = Object.fromEntries(Object.entries(formValues).filter(([, v]) => v !== ''));
      activeParameters.forEach((field) => {
        const mode = parameterModes[field];
        if (mode === 'inherit') {
          cleanedValues[field] = null;
        }
      });

      const fieldsAutomatic = ['cp_corr', 'yaw_offset', 'wheel_circumference'];
      const autocalList: string[] = [];
      fieldsAutomatic.forEach((field) => {
        const mode = parameterModes[field];
        if (mode === 'automatic') {
          autocalList.push(field);
        }
        // It is not clear if this is needed. It is designed to prevent other values from becoming manual
        // else if(mode === 'inherit'){
        //   cleanedValues[field] = null;  // Otherwise will be read as manual
        // }
      });
      cleanedValues['autocal_list'] = autocalList;

      // Map chain model fields to drivetrain dict and model selection
      const ftc = cleanedValues['front_tooth_count'];
      cleanedValues['drivetrain'] = {
        front_tooth_count: Number(ftc),
      };
      const mcId = cleanedValues['model_chain_id'];
      cleanedValues['model_chain_id'] = mcId || null;
      delete cleanedValues['front_tooth_count'];
      delete cleanedValues['chain_model'];

      // Remove fields that should not be sent in update payload
      delete cleanedValues['autocal_branch'];
      delete cleanedValues['parameters_inherited'];
      delete cleanedValues['parameters_manual'];

      const updatedCalibration = await updateCalibration.mutateAsync(cleanedValues);

      // Clear unsaved changes state after successful save
      clearUnsavedChanges();

      if (isNew) {
        if (isInSessionContext) {
          redirectAfterSave();
        } else {
          navigate(`/calibrations/${updatedCalibration.id}`);
        }
      } else {
        if (isInSessionContext) {
          redirectAfterSave();
        } else {
          setCalibration({ ...formValues, ...updatedCalibration });
        }
      }
    } catch (e) {
      console.error(e);
      toast.error('Unknown error saving calibration');
    }
  };

  const CalibrationActionButtons = () => {
    return (
      <div className="cr-flex cr-flex-row cr-gap-1">
        <Button onClick={formMethods.handleSubmit(save)} icon={<HiOutlineCheckCircle className={'cr-size-7'} />}>
          Save
        </Button>
      </div>
    );
  };

  return (
    <FormProvider {...formMethods}>
      <SectionHeader
        title={isNew ? 'New Calibration' : 'Calibration Configuration'}
        backLink={getBackLink()}
        isLoading={updateCalibration.isPending}
      />
      <fieldset disabled={updateCalibration.isPending}>
        <div className="cr-flex cr-flex-row cr-gap-4 cr-p-4 cr-font-main cr-text-[16px] cr-text-lg">
          <CalibrationActionButtons />
          <Spinner isFetching={updateCalibration.isPending} />
        </div>
        <div className="cr-flex cr-flex-col cr-rounded-lg cr-bg-white cr-p-2 cr-text-center cr-shadow-md md:cr-w-[640px] md:cr-text-left">
          <div className="cr-mb-4 cr-border-b cr-border-gray-200 cr-pb-2">
            <h3 className="font-montserrat cr-text-[22px] cr-font-semibold cr-uppercase cr-text-[#03365D]">
              Basic Information
            </h3>
          </div>
          <FormEntry label="Description">
            <FormInput fieldName="description" required="Description is Required" customClass="cr-w-full" />
          </FormEntry>

          <FormEntry label="Tags">
            <FormInput fieldName="tags" customClass="cr-w-full" />
          </FormEntry>

          <FormEntry label="Copy From">
            <div className="cr-space-y-3">
              {/* Default Team Calibration Option */}
              <div className="cr-flex cr-items-center cr-gap-2">
                <input
                  type="radio"
                  id="copy-default"
                  name="copy-source"
                  value="default"
                  checked={copySource === 'default'}
                  onChange={(e) => setCopySource(e.target.value as 'default')}
                  className="cr-mr-2"
                />
                <label htmlFor="copy-default" className="cr-text-sm cr-font-medium">
                  Copy from Default Team Calibration
                </label>
              </div>

              {/* Session-based Selection */}
              <div className="cr-space-y-2">
                <div className="cr-flex cr-items-center cr-gap-2">
                  <input
                    type="radio"
                    id="copy-session"
                    name="copy-source"
                    value="session"
                    checked={copySource === 'session'}
                    onChange={(e) => {
                      setCopySource(e.target.value as 'session');
                      // Clear session selection when switching modes
                      setValue('source_session_id', '', { shouldDirty: false });
                      setValue('copy_calibration_id', '', { shouldDirty: false });
                    }}
                    className="cr-mr-2"
                  />
                  <label htmlFor="copy-session" className="cr-text-sm cr-font-medium">
                    Copy from Session Calibration
                  </label>
                </div>

                {/* Session Selection - only show if session option is selected */}
                {copySource === 'session' && (
                  <div className="cr-ml-6 cr-space-y-2">
                    <FormEntry label="Select Session">
                      <FormInput
                        fieldName="source_session_id"
                        options={Array.from(sessions || [])
                          .filter((session) => session.analysis_type >= SessionAnalysisTypeUtils.singletonLevel())
                          .reverse()}
                        optionLabelFn={(session) => `${session.date} - ${session.description}`}
                        emptyOption="Select a Session"
                        customClass="cr-w-full"
                      />
                    </FormEntry>

                    {selectedSessionId && (
                      <>
                        <FormEntry label="Select Calibration">
                          <FormInput
                            fieldName="copy_calibration_id"
                            options={sessionConfigs}
                            optionLabelFn={(config) =>
                              `${config.letter || config.id} - ${config.description} (${config.combined_mass}
                               kg, CRR: ${config.crr})`
                            }
                            emptyOption="Select a Calibration from Session"
                            customClass="cr-w-full"
                          />
                        </FormEntry>

                        {watch('copy_calibration_id') && (
                          <div className="cr-mt-2">
                            <Button onClick={() => applyCopiedCalibration()} className="cr-w-full">
                              Apply Selected Calibration
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </FormEntry>
        </div>

        <div className="cr-mt-4 cr-flex cr-flex-col cr-rounded-lg cr-bg-white cr-p-2 cr-text-center cr-shadow-md md:cr-w-[640px] md:cr-text-left">
          <div className="cr-mb-4 cr-border-b cr-border-gray-200 cr-pb-2">
            <h3 className="font-montserrat cr-text-[22px] cr-font-semibold cr-uppercase cr-text-[#03365D]">
              Calibration Parameters
            </h3>
          </div>

          {/* Lock/Clear Calibration Buttons */}
          <div className="cr-mb-4 cr-flex cr-gap-2">
            <div className="cr-flex cr-items-center cr-gap-2">
              <Button
                onClick={() => lockCalibration()}
                className="cr-min-w-[150px] !cr-justify-center cr-px-4 !cr-pl-0 !cr-pr-0"
              >
                Set All Manual
              </Button>
              <div className="cr-relative">
                <button
                  type="button"
                  onMouseEnter={() => setActiveTooltip('lock-calibration')}
                  onMouseLeave={() => setActiveTooltip(null)}
                  className="cr-rounded cr-p-1 hover:cr-bg-gray-100"
                  title="This 'Locks' the calibration by setting all calibration modes to manual and inserting the current value"
                >
                  <HiOutlineInformationCircle className="cr-h-4 cr-w-4 cr-text-blue-500" />
                </button>
                {activeTooltip === 'lock-calibration' && (
                  <div className="cr-absolute cr-bottom-full cr-right-0 cr-z-10 cr-mb-2 cr-w-64 cr-rounded cr-bg-gray-800 cr-px-3 cr-py-2 cr-text-sm cr-text-white cr-shadow-lg">
                    Sets all calibration modes to manual and inserts the current value
                    <div className="cr-absolute cr-right-4 cr-top-full cr-h-0 cr-w-0 cr-border-l-4 cr-border-r-4 cr-border-t-4 cr-border-l-transparent cr-border-r-transparent cr-border-t-gray-800"></div>
                  </div>
                )}
              </div>
            </div>

            <div className="cr-flex cr-items-center cr-gap-2">
              <Button
                onClick={() => clearCalibration()}
                disabled={!calibration?.baseline_id}
                className="cr-min-w-[150px] !cr-justify-center cr-px-4 !cr-pl-0 !cr-pr-0"
              >
                Set All Inherit
              </Button>
              <div className="cr-relative">
                <button
                  type="button"
                  onMouseEnter={() => setActiveTooltip('clear-calibration')}
                  onMouseLeave={() => setActiveTooltip(null)}
                  className="cr-rounded cr-p-1 hover:cr-bg-gray-100"
                  title="This 'Clears' the calibration by setting all calibration modes to inherit. Requires a baseline calibration to be available.
                  Calibration will no longer be visible unless a 'branch' is created"
                >
                  <HiOutlineInformationCircle className="cr-h-4 cr-w-4 cr-text-blue-500" />
                </button>
                {activeTooltip === 'clear-calibration' && (
                  <div className="cr-absolute cr-bottom-full cr-right-0 cr-z-10 cr-mb-2 cr-w-64 cr-rounded cr-bg-gray-800 cr-px-3 cr-py-2 cr-text-sm cr-text-white cr-shadow-lg">
                    Sets all calibration modes to inherit. Requires a baseline calibration to be available.
                    <div className="cr-absolute cr-right-4 cr-top-full cr-h-0 cr-w-0 cr-border-l-4 cr-border-r-4 cr-border-t-4 cr-border-l-transparent cr-border-r-transparent cr-border-t-gray-800"></div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <FormEntry label="Lock Calibration">
            <div className="cr-flex cr-items-center cr-gap-2">
              <input
                type="checkbox"
                {...formMethods.register('autocal_lock')}
                className="cr-h-4 cr-w-4 cr-rounded cr-border-gray-300 cr-text-blue-600 focus:cr-ring-blue-500"
              />
              <span className="cr-text-sm cr-text-gray-700">Prevent automatic calibration updates</span>
            </div>
          </FormEntry>

          {renderFieldWithMode('combined_mass', 'Combined Mass', {
            inputType: 'number',
            required: 'Combined Mass is Required',
          })}

          {renderFieldWithMode('crr', 'CRR', { inputType: 'number' })}

          {renderFieldWithMode('power_meter_slope', 'Power Meter Slope', { inputType: 'number' })}

          {/* Chain Drag Model */}
          <FormEntry label="Chain Drag Model">
            <div className="cr-flex cr-flex-col cr-gap-2">
              <div className="cr-flex cr-items-center cr-gap-2">
                <span className="cr-text-sm cr-text-gray-600">
                  Model:
                </span>
                <select
                  {...formMethods.register('model_chain_id')}
                  className={
                    'cr-rounded cr-border cr-border-gray-300'
                    + ' cr-px-3 cr-py-1 cr-text-sm'
                    + ' cr-text-blue-900'
                  }
                >
                  <option value="">Select a Model</option>
                  {chainModels?.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                      {m.is_default ? ' (Default)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="cr-flex cr-items-center cr-gap-2">
                <input
                  type="number"
                  min={34}
                  max={70}
                  step={1}
                  {...formMethods.register(
                    'front_tooth_count' as any,
                    { valueAsNumber: true },
                  )}
                  className={
                    'cr-w-24 cr-rounded cr-border '
                    + 'cr-border-gray-300 cr-px-3 cr-py-2 '
                    + 'cr-text-sm'
                  }
                />
                <span className="cr-text-sm cr-text-gray-600">
                  teeth (front chainring)
                </span>
              </div>
            </div>
          </FormEntry>

          <FormEntry label="Speed Source">
            <div className="cr-flex cr-flex-row cr-gap-4">
              {['1', '2', 'GPS'].map((value) => (
                <label key={value} className="cr-flex cr-items-center cr-gap-2">
                  <input
                    type="radio"
                    value={value}
                    checked={speedInputType === value}
                    onChange={(e) => setSpeedInputTypeWrapper(e.target.value)}
                    className="cr-mr-2"
                  />
                  <span className="cr-text-sm">{value}</span>
                </label>
              ))}
            </div>
          </FormEntry>

          <FormEntry label="Power Source">
            <div className="cr-flex cr-flex-row cr-gap-4">
              {['1', '2', 'Combine'].map((value) => (
                <label key={value} className="cr-flex cr-items-center cr-gap-2">
                  <input
                    type="radio"
                    value={value}
                    checked={powerInputType === value}
                    onChange={(e) => setPowerInputTypeWrapper(e.target.value)}
                    className="cr-mr-2"
                  />
                  <span className="cr-text-sm">{value}</span>
                </label>
              ))}
            </div>
          </FormEntry>

          {renderFieldWithMode('cp_corr', 'CP Correction', { inputType: 'number' })}

          {renderFieldWithMode('yaw_offset', 'Probe Alignment', { inputType: 'number' })}

          {renderFieldWithMode('wheel_circumference', 'Wheel Circumference', { inputType: 'number' })}

          {renderFieldWithMode('cda_ref', 'CdA Reference', { inputType: 'number' })}

          {renderFieldWithMode('chest_ref', 'Chest Reference', { inputType: 'number' })}

          {renderFieldWithMode('head_ref', 'Head Reference', { inputType: 'number' })}

          {renderFieldWithMode('chest_slope', 'Chest Slope', { inputType: 'number' })}

          {renderFieldWithMode('head_slope', 'Head Slope', { inputType: 'number' })}

          {renderFieldWithMode('airspeed_ref', 'Airspeed Reference', { inputType: 'number' })}

          {renderFieldWithMode('airspeed_slope', 'Airspeed Slope', { inputType: 'number' })}

          {renderFieldWithMode('xw_wheel_compensation', 'Wheel Height Compensation', { inputType: 'number' })}

          {renderFieldWithMode('xw_para_slope', 'Crosswind Slope', { inputType: 'number' })}

          {renderFieldWithMode('xw_stall_slope', 'Crosswind Stall Slope', { inputType: 'number' })}

          {renderFieldWithMode('xw_stall_angle', 'Crosswind Stall Angle', { inputType: 'number' })}
        </div>
        <div className="cr-flex cr-flex-row cr-gap-4 cr-p-4 cr-font-main cr-text-[16px] cr-text-lg">
          <CalibrationActionButtons />
        </div>
      </fieldset>
    </FormProvider>
  );
}
